import { useRef, FC, RefObject, useEffect, useState } from "react";
import { Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton, Grid, FormControl, InputLabel, Select, MenuItem, Box, FormControlLabel, Checkbox, InputAdornment } from "@mui/material";
import { PaperProps } from "@mui/material/Paper";
import { useTranslation } from "react-i18next";
import Draggable from "react-draggable";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { countries } from 'utils/Countries';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider, DateTimePicker, DatePicker } from '@mui/x-date-pickers-pro';

interface InfoHolidayProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    selected: any;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const InfoHoliday: FC<InfoHolidayProps> = ({ open, setOpen, selected }) => {
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [public_name, setPublicName] = useState('')
    const [year, setYear] = useState(dayjs().year())
    const [country, setCountry] = useState('')
    const [is_renounceable, setIsRenounceable] = useState(true)
    const [is_local, setIsLocal] = useState(true)
    const [start, setStart] = useState(dayjs().startOf('day').toDate())
    const [end, setEnd] = useState(dayjs().startOf('day').toDate())

    useEffect(() => {
        if (open) {
            setName(selected[0]?.name || '');
            setPublicName(selected[0]?.public_name || '');
            setYear(selected[0]?.year || dayjs().year());
            setCountry(selected[0]?.country || '');
            setIsRenounceable(selected[0]?.is_renounceable || false);
            setIsLocal(selected[0]?.is_local || false);
            setStart(dayjs(selected[0]?.start_date).isValid() ? dayjs(selected[0]?.start_date).toDate() : dayjs().startOf('day').toDate());
            setEnd(dayjs(selected[0]?.end_date).isValid() ? dayjs(selected[0]?.end_date).toDate() : dayjs().startOf('day').toDate());
        }
    }, [open, selected]);

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            PaperComponent={PaperComponent}
            maxWidth="sm"
            fullWidth
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">{t('settings.holidays.modals.titles.info')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            name="name"
                            disabled
                            label={t('settings.holidays.modals.inputs.name')}
                            value={name}
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${name.length}/100`}</InputAdornment>
                                }
                            }}
                            fullWidth
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            name="public_name"
                            label={t('settings.holidays.modals.inputs.public_name')}
                            value={public_name}
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${public_name.length}/100`}</InputAdornment>
                                }
                            }}
                            disabled
                            fullWidth
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
                            <DatePicker
                                sx={{ width: '100%' }}
                                views={['year']}
                                label={t('settings.holidays.modals.inputs.year')}
                                name="year"
                                value={year ? dayjs().set('year', year) : dayjs()}
                                disabled
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <FormControl required fullWidth>
                            <InputLabel id="country-select-label">{t('settings.holidays.modals.inputs.country')}</InputLabel>
                            <Select
                                fullWidth
                                labelId="country-select-label"
                                id="country-select"
                                name="country"
                                label={t('settings.holidays.modals.inputs.country')}
                                value={country}
                                disabled
                            >
                                {countries.map((country) => (
                                    <MenuItem key={country.code} value={country.code}>
                                        <Box
                                            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                        >
                                            <img
                                                loading="lazy"
                                                width="30"
                                                src={`https://flagcdn.com/${country.code.toLowerCase()}.svg`}
                                                alt=""
                                            />
                                            {country.label} ({country.code})
                                        </Box>
                                    </MenuItem>
                                ))}

                            </Select>
                        </FormControl>
                    </Grid>

                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <DateTimePicker
                                sx={{ width: '100%' }}
                                label={t('settings.holidays.modals.inputs.start')}
                                name="start"
                                value={dayjs(start).isValid() ? dayjs(start) : null}
                                disabled
                                ampm={false}
                                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <DateTimePicker
                                sx={{ width: '100%' }}
                                label={t('settings.holidays.modals.inputs.end')}
                                name="end"
                                disabled
                                value={dayjs(end).isValid() ? dayjs(end) : null}
                                slotProps={{ textField: { required: true } }}
                                ampm={false}
                                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <FormControlLabel
                                control={<Checkbox
                                    name="is_renounceable"
                                    checked={is_renounceable}
                                    disabled
                                />}
                                label={t('settings.holidays.modals.inputs.is_renounceable')}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <FormControlLabel
                                control={<Checkbox
                                    name="is_local"
                                    checked={is_local}
                                    disabled
                                />}
                                label={t('settings.holidays.modals.inputs.is_local')}
                            />
                        </Grid>
                    </LocalizationProvider>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)} color="primary" variant="contained" disableElevation>
                    {t('settings.holidays.modals.buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export { InfoHoliday }